import { Flex, View, tokens } from '@sapphire-react/core';
import { Fragment, ReactNode } from 'react';
import { labelWeight } from '../../types/affordability';
import { formatMoney } from '../../utils/formatMoney';
import { ChartLegend } from './ChartLegend';
import { Donut } from './Donut';

export type ChartItem = {
  value?: number | null;
  color?: string;
  legendTitle: string;
  legendSubtitle?: string;
  contextualHelpContent?: ReactNode;
  testId?: string;
  displayLabel?: string;
  labelWeight?: labelWeight;
};

type Props = {
  chartItems: ChartItem[];
  donutContent: ReactNode;
  donutPreContent?: ReactNode;
  donutWidth?: number;
  donutHeight?: number;
  interactive?: boolean;
  currency?: string;
};

export function DonutChart({
  chartItems,
  donutContent,
  donutPreContent,
  donutWidth,
  donutHeight,
  interactive = false,
  currency = 'kr',
}: Props) {
  const total = chartItems.reduce((prev, curr) => prev + (curr.value ?? 0), 0);
  const segments = total
    ? chartItems.map((item) => ({
        percentage: ((item.value ?? 0) * 100) / total,
        color: item.color,
        label: item.legendTitle,
        formattedValue:
          item.displayLabel ?? formatMoney(item.value ?? 0, { currency }),
      }))
    : [];

  return (
    <View
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      width={'100%'}
      marginTop={tokens.size.spacingMd}
      UNSAFE_style={{ boxSizing: 'border-box' }}
      border={`1px solid ${tokens.color.borderPrimary.value}`}
      paddingBottom={tokens.size.spacingXl}
      borderRadius={tokens.size.radiusSm.value}
    >
      {donutPreContent}
      <Donut
        segments={segments}
        donutWidth={donutWidth}
        donutHeight={donutHeight}
        interactive={interactive}
      >
        {donutContent}
      </Donut>
      <Flex
        flexDirection={'column'}
        width={'100%'}
        paddingLeft={tokens.size.spacing2xl}
        paddingRight={tokens.size.spacing2xl}
        UNSAFE_style={{ boxSizing: 'border-box' }}
      >
        {chartItems.map((item, index) => (
          <Fragment key={`legend-${index}`}>
            <ChartLegend chartItem={item} />
          </Fragment>
        ))}
      </Flex>
    </View>
  );
}
